// window.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
 
export interface OpenOptions {
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
  features?: string; // e.g., "noopener,noreferrer,width=800,height=600"
  replace?: boolean; // if true, replaces the current page in session history
}
 
@Injectable({ providedIn: 'root' })
export class WindowService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}
 
  /** Returns the native window if available (browser only), otherwise undefined (SSR) */
  get native(): Window | undefined {
    return isPlatformBrowser(this.platformId) ? window : undefined;
  }
 
  /**
   * Open a URL using window.open in a safe, testable way.
   * Defaults to `_blank` + `noopener,noreferrer` for security.
   */
  open(url: string, opts: OpenOptions = {}): Window | null {
    const w = this.native;
    if (!w) {
      // running on SSR or non-browser environment
      return null;
    }
 
    const target = opts.target ?? '_blank';
 
    // Security best practices: add noopener,noreferrer if opening in new tab
    const baseFeatures =
      target === '_blank' ? 'noopener,noreferrer' : '';
 
    const features =
      [baseFeatures, opts.features].filter(Boolean).join(',');
 
    // `replace` is not a parameter of window.open; if replace=true use location.assign/replace
    if (target === '_self' && opts.replace) {
      w.location.replace(url);
      return null;
    }
 
    return w.open(url, target, features);
  }
}